// F13 Wave B · Shared marketing primitives for Product + Docs + Resources.
// F14 · Task 1 · V11 palette (Vercel + Linear + OpenAI).
// F15 · Task 1 · V12 Vercel super-minimal monochrome.
//
// All primitives live inside <MainLayout> (HeaderV4 + FooterV4) via
// App.js route configuration. Backward-compat: all props/API unchanged.

import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Clock, Circle } from "lucide-react";
import { CrossStackLink } from "../shared/CrossStackLink";

// --- StatusBadge ---------------------------------------------------------
// F15 pattern: monochrome text + border, dot indicator is the only color.
// Available  → teal dot (#D4D4D4) · everything else monochrome.

const STATUS_MAP = {
    available: {
        label: "Available",
        icon: Check,
        dotStyle: { backgroundColor: "#D4D4D4" },
        textColor: "#CCCCCC",
    },
    early: {
        label: "Early access",
        icon: Clock,
        dotStyle: { backgroundColor: "#71717A" },
        textColor: "#CCCCCC",
    },
    roadmap: {
        label: "In roadmap",
        icon: Circle,
        dotStyle: { backgroundColor: "transparent", border: "1px solid #71717A" },
        textColor: "#999999",
    },
};

export function StatusBadge({ status = "roadmap", label, compact = false, testid }) {
    const { t } = useTranslation("f13_pages");
    const s = STATUS_MAP[status] || STATUS_MAP.roadmap;
    const displayLabel = label || t(`status.${status}`, { defaultValue: s.label });
    const sizeCls = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
    const dotSize = compact ? "w-1 h-1" : "w-1.5 h-1.5";
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-full border border-[#1A1A1A] bg-[#111111] ${sizeCls} uppercase tracking-widest font-semibold`}
            style={{ color: s.textColor }}
        >
            <span className={`${dotSize} rounded-full shrink-0`} style={s.dotStyle} />
            {displayLabel}
        </span>
    );
}

// --- ProductHero ---------------------------------------------------------

export function ProductHero({
    eyebrow,
    title,
    subtitle,
    status,
    primaryCTA,
    secondaryCTA,
    testid,
}) {
    return (
        <section
            data-testid={testid || "product-hero"}
            className="max-w-6xl mx-auto px-6 pt-16 pb-12 lg:pt-24"
        >
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-3xl"
            >
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    {eyebrow && (
                        <span
                            data-testid="product-hero-eyebrow"
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#1A1A1A] bg-[#111111] text-xs text-[#CCCCCC] uppercase tracking-widest"
                        >
                            {eyebrow}
                        </span>
                    )}
                    {status && <StatusBadge status={status} />}
                </div>
                <h1
                    data-testid="product-hero-title"
                    className="text-4xl lg:text-6xl font-bold tracking-tight mb-6 text-white"
                >
                    {title}
                </h1>
                <p
                    data-testid="product-hero-subtitle"
                    className="text-xl text-[#CCCCCC] leading-relaxed mb-8"
                >
                    {subtitle}
                </p>
                {(primaryCTA || secondaryCTA) && (
                    <div className="flex flex-wrap gap-3">
                        {primaryCTA}
                        {secondaryCTA}
                    </div>
                )}
            </motion.div>
        </section>
    );
}

// --- CTA buttons ---------------------------------------------------------
// F15 pattern: Primary = white-on-black (Vercel homepage).
//              Secondary = border + transparent, text white.
// FASE I WARN cleanup round-1 · Delegates routing choice (SPA · SSR ·
// external) to `CrossStackLink` so cross-stack CTAs force a full page
// reload.
// FASE I WARN cleanup round-2 · Unifies `to` and `href` props under the
// shared helper (previously `href` bypassed CrossStackLink and lost
// `rel="noopener"` on `external` legacy links → reverse tabnabbing CVE
// surface). External URLs are now auto-detected by `isExternal()` and
// receive the correct `rel="noopener noreferrer"` + `target="_blank"`.

export function PrimaryBtn({ to, href, children, testid, className, ...rest }) {
    const cls = `inline-flex items-center gap-2 rounded-md bg-white hover:bg-[#EDEDED] text-black text-sm font-medium px-4 py-2.5 transition-colors no-underline${className ? ` ${className}` : ""}`;
    const arrow = <ArrowRight className="h-4 w-4 rtl:scale-x-[-1]" />;
    const target = to || href;
    if (!target) {
        return (
            <button type="button" data-testid={testid} className={cls} {...rest}>
                {children}{arrow}
            </button>
        );
    }
    return (
        <CrossStackLink to={target} data-testid={testid} className={cls} {...rest}>
            {children}{arrow}
        </CrossStackLink>
    );
}

export function SecondaryBtn({ to, href, children, testid, className, ...rest }) {
    const cls = `inline-flex items-center gap-2 rounded-md border border-[#1F1F1F] hover:border-[#2A2A2A] hover:bg-[#111] bg-transparent text-white text-sm font-medium px-4 py-2.5 transition-colors no-underline${className ? ` ${className}` : ""}`;
    const target = to || href;
    if (!target) {
        return (
            <button type="button" data-testid={testid} className={cls} {...rest}>
                {children}
            </button>
        );
    }
    return (
        <CrossStackLink to={target} data-testid={testid} className={cls} {...rest}>
            {children}
        </CrossStackLink>
    );
}

// --- Section ------------------------------------------------------------

export function Section({ title, subtitle, children, testid, dense = false }) {
    return (
        <section
            data-testid={testid}
            className={`max-w-6xl mx-auto px-6 ${dense ? "py-10" : "py-16"}`}
        >
            {title && (
                <div className="mb-10 max-w-3xl">
                    <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-3">{title}</h2>
                    {subtitle && <p className="text-base text-[#CCCCCC] leading-relaxed">{subtitle}</p>}
                </div>
            )}
            {children}
        </section>
    );
}

// --- FeatureCard + FeatureGrid ------------------------------------------

export function FeatureCard({ icon: Icon, title, desc, status, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-xl p-5 border border-[#1A1A1A] bg-[#111] hover:border-[#2A2A2A] hover:-translate-y-0.5 transition-all h-full"
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                {Icon && (
                    <div
                        className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-[#1F1F1F] bg-[#161616]"
                    >
                        <Icon className="h-5 w-5 text-white" />
                    </div>
                )}
                {status && <StatusBadge status={status} compact />}
            </div>
            <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
            {desc && <p className="text-sm text-[#CCCCCC] leading-relaxed">{desc}</p>}
        </div>
    );
}

export function FeatureGrid({ items, columns = 3, testid }) {
    const colsMap = {
        2: "grid-cols-1 md:grid-cols-2",
        3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
        4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    };
    return (
        <div data-testid={testid} className={`grid gap-4 ${colsMap[columns] || colsMap[3]}`}>
            {items.map((item, i) => (
                <FeatureCard key={i} {...item} />
            ))}
        </div>
    );
}

// --- ComparisonTable ----------------------------------------------------

export function ComparisonTable({ headers, rows, testid, highlightCol = null }) {
    return (
        <div
            data-testid={testid}
            className="overflow-x-auto rounded-xl border border-[#1A1A1A]"
        >
            <table className="w-full text-sm">
                <thead className="bg-[#111]">
                    <tr>
                        {headers.map((h, i) => (
                            <th
                                key={i}
                                className={`text-left px-4 py-3 font-medium text-[#CCCCCC] border-b border-[#1A1A1A] whitespace-nowrap ${i === highlightCol ? "text-white" : ""}`}
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className="border-b border-[#141414] hover:bg-[#111]">
                            {row.map((cell, j) => (
                                <td
                                    key={j}
                                    className={`px-4 py-3 align-top ${j === highlightCol ? "text-white font-medium" : "text-[#EDEDED]"}`}
                                >
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// --- StepFlow -----------------------------------------------------------

export function StepFlow({ steps, testid }) {
    return (
        <div data-testid={testid} className="overflow-x-auto">
            <div className="inline-flex items-center gap-2 min-w-full">
                {steps.map((step, i) => (
                    <React.Fragment key={i}>
                        <div className="flex-1 min-w-[140px] rounded-lg px-4 py-3 border border-[#1A1A1A] bg-[#111] text-center">
                            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">
                                Step {i + 1}
                            </div>
                            <div className="text-sm font-medium text-white">{step}</div>
                        </div>
                        {i < steps.length - 1 && (
                            <ArrowRight className="h-4 w-4 text-[#71717A] shrink-0 rtl:scale-x-[-1]" />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

// --- CodeBlock ----------------------------------------------------------

export function CodeBlock({ children, label, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-lg border border-[#1A1A1A] overflow-hidden bg-black"
        >
            {label && (
                <div className="px-4 py-2 border-b border-[#1A1A1A] bg-[#111] text-[10px] uppercase tracking-widest text-[#71717A]">
                    {label}
                </div>
            )}
            <pre className="p-4 overflow-x-auto text-sm text-[#EDEDED] font-mono leading-relaxed whitespace-pre">
                {children}
            </pre>
        </div>
    );
}

// --- CtaStrip -----------------------------------------------------------
// F15: moved from indigo→purple gradient to subtle surface + inverted CTA.

export function CtaStrip({ title, subtitle, primary, secondary, testid }) {
    return (
        <section data-testid={testid || "cta-strip"} className="max-w-6xl mx-auto px-6 pb-24">
            <div
                className="rounded-2xl px-6 py-8 lg:px-10 lg:py-10 border border-[#1F1F1F] flex flex-col lg:flex-row items-start lg:items-center gap-6 justify-between"
                style={{ backgroundImage: "linear-gradient(180deg, #111111 0%, #0A0A0A 100%)" }}
            >
                <div className="max-w-2xl">
                    <h3 className="text-xl lg:text-2xl font-semibold text-white mb-2">{title}</h3>
                    {subtitle && <p className="text-sm text-[#CCCCCC] leading-relaxed">{subtitle}</p>}
                </div>
                <div className="flex flex-wrap gap-3 shrink-0">
                    {primary}
                    {secondary}
                </div>
            </div>
        </section>
    );
}

// --- PageShell ---------------------------------------------------------

export function PageShell({ children, testid }) {
    return (
        <div
            data-testid={testid}
            className="min-h-screen bg-[#0A0A0A] text-white"
        >
            {children}
        </div>
    );
}
